import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  first,
  map,
  Observable,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { ConfirmModalComponent } from 'src/app/core/components/confirm-modal/confirm-modal.component';
import { Constants } from 'src/app/core/constants';
import { CustomFile } from 'src/app/core/models/customFile.interface';
import {
  Attachment,
  AttachmentWithDetails,
} from 'src/app/core/models/project/attachment/attachment.interface';
import { StorageUsage } from 'src/app/core/models/storage-usage.interface';
import { AttachmentService } from 'src/app/core/services/attachment.service';
import { FileService } from 'src/app/core/services/file.service';
import { TariffService } from 'src/app/core/services/tariff.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { Convertor } from 'src/app/core/utils/convertor';
import { Util } from 'src/app/core/utils/util';

@Component({
    selector: 'app-attachments',
    templateUrl: './attachments.component.html',
    styleUrls: ['./attachments.component.scss'],
    standalone: false
})
export class AttachmentsComponent implements OnChanges {
  private readonly fileService = inject(FileService);
  private readonly attachmentService = inject(AttachmentService);
  private readonly tariffService = inject(TariffService);
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(ToastService);
  private readonly modalService = inject(NgbModal);

  @Input({ required: true }) companyId!: string;
  @Input({ required: true }) projectId!: string;
  @Input({ required: true }) assignmentId!: string;
  @Input({ required: true }) attachmentsPath!: string;
  @Input() isTemporaryFiles = false;
  @Input() isReadonly = false;

  @Output() uploadedFileEvent = new EventEmitter<Attachment[]>();
  @Output() deletedFileEvent = new EventEmitter<Attachment[]>();

  @ViewChild('dangerTpl') dangerTpl!: TemplateRef<unknown>;

  private attachments$ = new BehaviorSubject<AttachmentWithDetails[]>([]);

  public readonly maxFileSize = Constants.RequestBodySize;
  public readonly currentLang: string;

  public readonly emptyFileListParam: Record<string, string> = {
    uk: 'файлах',
    en: 'File list',
  };

  public readonly storageLimitParam: Record<string, string> = {
    uk: 'сховища',
    en: 'storage',
  };

  public callbackError?: string;

  public canUploadFiles$ = this.tariffService.companyQuotas$.pipe(
    map((companyQuotas) => {
      const quotaKey = Convertor.pascalToScreamingSnakeCase(
        Constants.QuotaKeys.s3Size,
      );
      const maxStorageSizeQuota = companyQuotas.find(
        (q) => q.key === quotaKey,
      )!;

      if (!maxStorageSizeQuota) {
        return false;
      }

      return maxStorageSizeQuota.limit > maxStorageSizeQuota.used;
    }),
  );

  public readonly storageUsage$ = this.tariffService.companyQuotas$.pipe(
    map((companyQuotas): StorageUsage => {
      const quotaKey = Convertor.pascalToScreamingSnakeCase(
        Constants.QuotaKeys.s3Size,
      );
      const storageQuota = companyQuotas.find((q) => q.key === quotaKey)!;
      const limit = storageQuota?.limit ?? 0;
      const used = storageQuota?.used ?? 0;
      const available = Math.max(limit - used, 0);
      const usedPercent = limit > 0 ? Math.round((used / limit) * 1000) / 10 : 0;

      return {
        used,
        limit,
        available,
        usedPercent,
      };
    }),
    shareReplay(1),
  );

  public readonly files$ = this.attachments$.pipe(
    map((attachments) => {
      const files = attachments.filter((a) => !a.link);

      for (const file of files) {
        file.imageSrc = Util.getAttachmentImage(file.title);
        file.extensionName = Util.getExtensionFromFileTitle(file.title);
        file.fileActionName = Util.canBeOpenedFile(file) ? 'open' : 'download';
        file.uploadedByName = file.createdBy ?? null;
        file.uploadedAtLabel = this.formatAttachmentUploadedAt(file.createdAt);
      }

      return files;
    }),
    shareReplay(1),
  );

  constructor() {
    this.currentLang =
      this.translateService.currentLang ?? Constants.DefaultLanguage;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] || changes['assignmentId']) {
      if (!this.projectId || !this.assignmentId) {
        return;
      }

      this.attachmentService.setAttachmentsUrl(
        this.projectId,
        this.assignmentId,
      );

      this.reloadAssignmentAttachments();
    }
  }

  public reloadAssignmentAttachments(): void {
    this.attachmentService
      .getAssignmentAttachments()
      .pipe(
        first(),
        tap((attachments) => this.attachments$.next(attachments)),
      )
      .subscribe();
  }

  public deleteAttachment(id: string): void {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      size: 'sm',
    });

    modalRef.componentInstance.message = Util.translateWithParam(
      this.translateService,
      `WARNINGS.DELETE`,
      {
        uk: 'файл',
        en: 'file',
      },
    );

    modalRef.closed
      .pipe(
        first(),
        filter((res) => !!res),
        switchMap(() => {
          const attachments = this.attachments$.getValue();
          const fileToDelete = attachments.find((f) => f.id === id)!;

          if (this.isTemporaryFiles) {
            const updatedAttachments = attachments.filter(
              (a) => a.id !== fileToDelete.id,
            );

            this.attachments$.next(updatedAttachments);
            this.deletedFileEvent.emit(updatedAttachments);

            return EMPTY;
          } else {
            return this.fileService
              .delete(
                fileToDelete.s3Key ?? `${this.attachmentsPath}/${Util.getFileFullName(fileToDelete)}`,
              )
              .pipe(
                first(),
                switchMap(() => this.attachmentService.deleteAttachment(id)),
                tap(() => {
                  const updatedAttachments = attachments.filter(
                    (a) => a.id !== id,
                  );
                  this.attachments$.next(updatedAttachments);
                }),
              );
          }
        }),
      )
      .subscribe();
  }

  public handleFileAction(file: Attachment, action: 'open' | 'download'): void {
    const path = file.s3Key ?? `${this.attachmentsPath}/${Util.getFileFullName(file)}`;
    const traceOptions = {
      uploadId: file.uploadId,
      fileId: file.id,
    };

    const fileAction$: Observable<string | Blob> =
      action === 'open'
        ? this.fileService
            .getFileUrl(path, false, traceOptions)
            .pipe(tap((url) => window.open(url, '_blank')))
        : this.fileService
            .downloadFile(path, file.title, traceOptions)
            .pipe(tap((blob) => Util.saveAs(blob, file.title)));

    fileAction$
      .pipe(
        first(),
        catchError((error) => {
          console.log(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  public onUploadFile(event: CustomFile): void {
    if (!event.upload) {
      return;
    }

    const newFile = {
      id: Util.getGuidFromTitle(event.title),
      title: event.file.name,
      fileSize: event.file.size,
      companyId: this.companyId,
      uploadId: event.upload.uploadId,
      s3Bucket: event.upload.bucket,
      s3Key: event.upload.key,
      eTag: event.upload.eTag,
      versionId: event.upload.versionId,
      contentType: event.upload.contentType,
      createdAtUtc: event.upload.createdAtUtc,
    } as Attachment;

    const updatedAttachments = this.attachments$.getValue();
    updatedAttachments.push(newFile);

    if (this.isTemporaryFiles) {
      this.attachments$.next(updatedAttachments);
      this.uploadedFileEvent.emit(updatedAttachments);
    } else {
      this.attachmentService
        .createAssignmentAttachment(newFile, event.upload.correlationId)
        .pipe(
          first(),
          tap((createdAttachment) => {
            const createdFile = updatedAttachments.find((attachment) => attachment.id === createdAttachment.id);

            if (createdFile) {
              createdFile.createdAt = createdAttachment.createdAt ?? null;
              createdFile.createdBy = createdAttachment.createdBy ?? null;
              createdFile.uploadedByName = createdAttachment.createdBy ?? null;
              createdFile.uploadedAtLabel = this.formatAttachmentUploadedAt(createdAttachment.createdAt);
            }

            this.attachments$.next([...updatedAttachments]);
          }),
        )
        .subscribe();
    }
  }

  public onDeleteFile(event: CustomFile): void {
    const id = Util.getGuidFromTitle(event.title);

    if (!id) {
      return;
    }

    const attachments = this.attachments$.getValue().filter((a) => a.id !== id);

    if (this.isTemporaryFiles) {
      this.attachments$.next(attachments);
      this.deletedFileEvent.emit(attachments);
    } else {
      this.attachmentService
        .deleteAttachment(id)
        .pipe(
          first(),
          tap(() => this.attachments$.next(attachments)),
        )
        .subscribe();
    }
  }

  public renameFile(id: string, newFileName: string): void {
    this.attachmentService
      .renameFileName(id, newFileName)
      .pipe(
        first(),
        tap(() => {
          const attachments = this.attachments$.getValue();
          const updatedAttachment = attachments.find((a) => a.id === id)!;

          updatedAttachment.mappedTitle = newFileName;

          this.attachments$.next([...attachments]);
        }),
      )
      .subscribe();
  }

  public onReachedStorageLimit(): void {
    const translatedMessage = this.translateService.instant(
      'TOOLTIPS.LIMIT_REACHED',
      { entity: this.storageLimitParam[this.currentLang] },
    );

    this.callbackError = translatedMessage;

    this.toastService.show({
      template: this.dangerTpl,
      className: 'bg-danger text-light',
    });
  }

  private formatAttachmentUploadedAt(createdAt?: string | null): string | null {
    if (!createdAt) {
      return null;
    }

    return new Intl.DateTimeFormat(this.translateService.currentLang || 'en', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(createdAt));
  }
}
